import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  MessageSquare,
  MessageCircle,
  FileText,
  Sparkles,
  BarChart3,
  Star,
  ArrowUpRight,
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const useCases = [
  { key: 'leads',    Icon: MessageSquare },
  { key: 'whatsapp', Icon: MessageCircle },
  { key: 'admin',    Icon: FileText },
  { key: 'content',  Icon: Sparkles },
  { key: 'reports',  Icon: BarChart3 },
  { key: 'reviews',  Icon: Star },
] as const;

const AIShowcase: React.FC = () => {
  const { t } = useLanguage();
  const ref = useRef<HTMLElement | null>(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <section
      id="ai"
      ref={ref}
      className="relative py-24 md:py-32 bg-secondary/40 border-y border-border/60 overflow-hidden"
      aria-labelledby="ai-heading"
    >
      {/* Atmospheric glows */}
      <div className="pointer-events-none absolute -top-40 -left-40 h-[28rem] w-[28rem] rounded-full bg-lime/[0.06] blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 h-[28rem] w-[28rem] rounded-full bg-primary/[0.10] blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.4]" aria-hidden="true" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="grid grid-cols-12 gap-6 mb-16"
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
          transition={{ duration: 0.8 }}
        >
          <div className="col-span-12 md:col-span-8">
            <span className="pill-tag-lime">
              <span className="h-1.5 w-1.5 rounded-full bg-lime animate-pulse" />
              {t('useCases.eyebrow')}
            </span>
            <h2
              id="ai-heading"
              className="mt-5 font-display text-foreground text-[clamp(2rem,4.2vw,3.75rem)] leading-[1.04] tracking-tight"
            >
              {t('useCases.title')}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 self-end">
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              {t('useCases.subtitle')}
            </p>
          </div>
        </motion.div>

        {/* Use-case grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {useCases.map(({ key, Icon }, idx) => (
            <motion.article
              key={key}
              className="group relative overflow-hidden rounded-2xl border border-border/80 bg-card/70 backdrop-blur-sm p-7 transition-all duration-500 hover:bg-card hover:border-lime/40 hover:-translate-y-1"
              initial={{ opacity: 0, y: 28 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 28 }}
              transition={{ duration: 0.6, delay: idx * 0.08 }}
            >
              <div className="pointer-events-none absolute -top-20 -right-20 h-48 w-48 rounded-full bg-lime/[0.10] blur-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />

              <header className="relative z-10 flex items-start justify-between mb-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-lime/30 bg-lime/[0.08] text-lime group-hover:bg-lime/[0.15] transition-colors">
                  <Icon className="h-5 w-5" strokeWidth={1.6} />
                </div>
                <span className="font-mono-tag text-[10px] uppercase tracking-[0.32em] text-muted-foreground/70">
                  / 0{idx + 1}
                </span>
              </header>

              <div className="relative z-10">
                <h3 className="font-display text-foreground text-2xl md:text-[1.625rem] leading-tight tracking-tight mb-3">
                  {t(`useCases.items.${key}.title`)}
                </h3>
                <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                  {t(`useCases.items.${key}.desc`)}
                </p>
              </div>
            </motion.article>
          ))}
        </div>

        {/* CTA strip */}
        <motion.div
          className="mt-14 md:mt-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 rounded-2xl border border-border/80 bg-card/60 backdrop-blur-sm p-6 md:p-10"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <div className="flex-1">
            <h3 className="font-display text-xl md:text-2xl text-foreground mb-1.5">
              {t('servicesAi.ready')}
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              {t('servicesAi.readyText')}
            </p>
          </div>
          <Link
            to="/services/ai"
            className="group inline-flex items-center justify-center gap-3 rounded-full border border-lime/40 bg-lime/[0.08] hover:bg-lime hover:text-lime-foreground transition-all duration-300 px-6 py-3.5 text-sm font-medium text-lime w-full md:w-auto"
          >
            <Sparkles className="h-4 w-4" />
            {t('nav.ai')}
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default AIShowcase;
